import React, { useEffect, useRef, useState } from "react";
import Markdown from "@/components/Markdown";
import ThinkingState from "@/components/ThinkingState";
import api, { apiErr } from "@/lib/api";
import { Bot, Send, Loader2, User } from "lucide-react";
import { toast } from "sonner";

const STARTERS = [
  "What does 'process over profit' really mean?",
  "How do I know where to put my stop?",
  "What is confluence?",
  "Why is passing on a trade a good decision?",
];

export default function Coach() {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { api.get("/coach/history").then((r) => setMessages(r.data.messages || [])); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const send = async (msg) => {
    const content = (msg ?? text).trim();
    if (!content || busy) return;
    setText("");
    setMessages((m) => [...m, { role: "user", text: content }]);
    setBusy(true);
    try {
      const { data } = await api.post("/coach/message", { text: content });
      setMessages((m) => [...m, { role: "coach", text: data.reply }]);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto flex flex-col" style={{ minHeight: "calc(100vh - 1px)" }}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-11 h-11 rounded-xl bg-tc-coach/15 border border-tc-coach/30 flex items-center justify-center text-tc-coach"><Bot className="w-6 h-6" /></div>
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Craft Coach</h1>
          <p className="text-tc-sub text-sm">Your AI tutor. It explains, it does not predict. Powered by Claude.</p>
        </div>
      </div>

      <div className="tc-card flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="coach-messages" style={{ maxHeight: "60vh" }}>
          {messages.length === 0 && (
            <div className="text-center py-8">
              <p className="text-tc-sub mb-4">Ask me anything about trading, charts, or your last trade.</p>
              <div className="grid sm:grid-cols-2 gap-2">
                {STARTERS.map((s) => (
                  <button key={s} data-testid="coach-starter" onClick={() => send(s)}
                    className="text-left text-sm text-tc-sub tc-card p-3 hover:border-tc-coach/40 transition-colors">{s}</button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${m.role === "user" ? "bg-white/10" : "bg-tc-coach/15 text-tc-coach"}`}>
                {m.role === "user" ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
              </div>
              {m.role === "user"
                ? <div className="rounded-xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap break-words max-w-[80%] bg-tc-coach text-tc-bg">{m.text}</div>
                : <Markdown text={m.text} className="rounded-xl px-4 py-2.5 text-sm leading-relaxed max-w-[80%] min-w-0 bg-white/5 text-tc-sub" testid={`coach-msg-${i}`} />}
            </div>
          ))}
          {busy && <div className="flex gap-3"><div className="w-8 h-8 rounded-lg bg-tc-coach/15 text-tc-coach flex items-center justify-center"><Bot className="w-4 h-4" /></div><div className="rounded-xl px-4 py-2.5 bg-white/5"><ThinkingState /></div></div>}
          <div ref={endRef} />
        </div>
        <div className="border-t border-white/10 p-3 flex gap-2">
          <input data-testid="coach-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Ask Craft Coach…" className="flex-1 rounded-lg bg-tc-bg border border-white/10 px-4 py-2.5 text-sm outline-none focus:border-tc-coach" />
          <button data-testid="coach-send" onClick={() => send()} disabled={busy}
            className="rounded-lg bg-tc-coach text-tc-bg px-4 hover:bg-tc-coach/90 disabled:opacity-50"><Send className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
}
